import { RESOURCE_NAMES } from "./resources.js";
/**
 * Static ship data from `docs/game-details/ships.md`. Every race fields a
 * variant of each of the 13 ship IDs; stats differ per race, but the
 * category, battle-skill slot (except ID 7) and target lists are shared
 * across the ID.
 */
/** The colony ship's id (Pioneer / Assembler / Propagator) — the same for every race. */
export const COLONY_SHIP_ID = 10;
// ---------------------------------------------------------------------------
// Construction-time modifiers (see "Ship Production Times" in ships.md)
// ---------------------------------------------------------------------------
/** Factory shaves this fraction off ship build time per level… */
export const SHIPYARD_TIME_REDUCTION_PER_LEVEL = 0.05;
/** …capped at 75% (hit at Factory level 15). */
export const SHIPYARD_TIME_REDUCTION_CAP = 0.75;
/** Automation Center (Automation Center / Superbot Yard / Hypermutation Cocoon) reduction per level… */
export const AUTOMATION_CENTER_TIME_REDUCTION_PER_LEVEL = 0.05;
/** …capped at 25% (hit at the building's max level, 5). */
export const AUTOMATION_CENTER_TIME_REDUCTION_CAP = 0.25;
/**
 * Cancelling an in-progress ship batch refunds a uniformly-rolled fraction
 * of the unfinished units' cost in this range (ships.md: "between 40% and
 * 60%"). The roll itself is injected by the caller — the game engine stays
 * deterministic.
 */
export const SHIP_BUILD_REFUND_MIN = 0.4;
export const SHIP_BUILD_REFUND_MAX = 0.6;
/**
 * Robots-only (issue #91): bundling multiple ship-build orders into one
 * `buildShipGroup` request. A "group" of 1 item is just `buildShips`, so the
 * minimum is 2; the max item count is a UI sanity cap, not a game-balance
 * number.
 */
export const SHIP_GROUP_MIN_ITEMS = 2;
export const SHIP_GROUP_MAX_ITEMS = 10;
/**
 * How long a finished batch's delivery can be held before it auto-drops onto
 * the planet — shared by Robots' ship-build delivery groups (issue #91) and
 * every race's Recovery Center batches (issue #21), per the original design
 * ("the ready process can remain in the building for not more than 48
 * hours... after that the ready resource is dropped" — buildings.md). Once a
 * batch finishes construction/processing (`endDate`), the player can deliver
 * it on demand at any point up to `endDate + AUTO_DELIVERY_HOLD_HOURS`; if
 * nobody does, the next tick/action that replays past that point auto-drops
 * it — see `deliverAt` on `ShipBuildDetails` / `ScrapRecycleDetails`.
 */
export const AUTO_DELIVERY_HOLD_HOURS = 48;
// ---------------------------------------------------------------------------
// Identity helpers
// ---------------------------------------------------------------------------
/** Orbital power plants (Orbital Power Plant / Photon Capacitor / Orbital Photosynthesizer) feed the planet's live energy income (resourceEngine). */
export const SOLAR_SATELLITE_SHIP_ID = 13;
export const ALL_SHIP_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
/** Damage reduction granted by each armor class (Light 3% / Medium 6% / Heavy 9%). */
export const ARMOR_TYPE_REDUCTION = {
    light: 0.03,
    medium: 0.06,
    heavy: 0.09,
};
// ---------------------------------------------------------------------------
// Definition table
// ---------------------------------------------------------------------------
const b = (id, level) => ({ type: "building", id, level });
const s = (id, level) => ({ type: "science", id, level });
/**
 * Priority / bonus / penalty target lists are identical across races when
 * expressed as ship IDs (e.g. every ID-3 fighter prioritizes the ID-5
 * Aegis / Fortress / Chiton triplet). Defense-structure priority targets (turrets, matrices,
 * tissues) are intentionally absent until defenses exist.
 */
const WAR_TARGETS = {
    3: { priorityTargets: [5], bonusTargets: [5, 6], penaltyTargets: [3, 9] },
    4: { priorityTargets: [3], bonusTargets: [3, 5], penaltyTargets: [4, 6] },
    5: { priorityTargets: [8], bonusTargets: [8, 9], penaltyTargets: [3, 5] },
    6: { priorityTargets: [4], bonusTargets: [4, 5], penaltyTargets: [6, 7] },
    7: { priorityTargets: [6], bonusTargets: [6, 9], penaltyTargets: [7, 8] },
    8: { priorityTargets: [7], bonusTargets: [7, 9], penaltyTargets: [4, 8] },
    9: { priorityTargets: [9], bonusTargets: [9, 4], penaltyTargets: [5] },
};
const HUMAN_SHIPS = {
    1: {
        id: 1, race: "humans", name: "Courier", category: "civilian",
        description: "Light transport ship for moving resources between planets",
        attack: 10, life: 2000, weaponType: "laser", armorType: "light",
        hangar: 1, cargo: 5000, speed: 24000, fuelConsumption: 12,
        buildTimeSeconds: 600,
        cost: { metal: 2400, minerals: 1400, gas: 0 },
        requirements: [b("shipyard", 2), s("mathematics", 2)],
    },
    2: {
        id: 2, race: "humans", name: "Juggernaut", category: "civilian",
        description: "Heavy transport ship with a vast cargo hold",
        attack: 10, life: 7800, weaponType: "laser", armorType: "medium",
        hangar: 3, cargo: 20000, speed: 19000, fuelConsumption: 45,
        buildTimeSeconds: 1200,
        cost: { metal: 6400, minerals: 5000, gas: 0 },
        requirements: [b("shipyard", 4), s("astronomy", 6)],
    },
    3: {
        id: 3, race: "humans", name: "Vanguard", category: "war",
        description: "Fast light fighter that bolsters the fleet's life",
        attack: 800, life: 2400, weaponType: "laser", armorType: "light",
        hangar: 2, cargo: 250, speed: 28000, fuelConsumption: 25,
        buildTimeSeconds: 1200,
        cost: { metal: 2400, minerals: 1600, gas: 0 },
        requirements: [b("shipyard", 1), s("astronomy", 1)],
        skills: ["bonusLife"], ...WAR_TARGETS[3],
    },
    4: {
        id: 4, race: "humans", name: "Saber", category: "war",
        description: "Versatile mid-line warship with devastating critical strikes",
        attack: 3080, life: 9200, weaponType: "ion", armorType: "light",
        hangar: 7, cargo: 800, speed: 32000, fuelConsumption: 315,
        buildTimeSeconds: 1540,
        cost: { metal: 10200, minerals: 8400, gas: 0 },
        requirements: [b("shipyard", 3), s("defenceSystems", 2), s("rockets", 2)],
        skills: ["devastate"], ...WAR_TARGETS[4],
    },
    5: {
        id: 5, race: "humans", name: "Aegis", category: "war",
        description: "Defensive warship that can slip past enemy armor",
        attack: 2760, life: 16200, weaponType: "laser", armorType: "light",
        hangar: 6, cargo: 1500, speed: 20000, fuelConsumption: 280,
        buildTimeSeconds: 2100,
        cost: { metal: 5300, minerals: 15900, gas: 0 },
        requirements: [b("shipyard", 5), s("rockets", 4), s("ion", 2)],
        skills: ["ignoreArmor"], ...WAR_TARGETS[5],
    },
    6: {
        id: 6, race: "humans", name: "Bull", category: "war",
        description: "Armored line-breaker that hardens allied hulls",
        attack: 9000, life: 27000, weaponType: "ion", armorType: "medium",
        hangar: 15, cargo: 1500, speed: 20000, fuelConsumption: 480,
        buildTimeSeconds: 3300,
        cost: { metal: 49400, minerals: 21200, gas: 0 },
        requirements: [b("shipyard", 7), s("aerodynamics", 4)],
        skills: ["armorBoost"], ...WAR_TARGETS[6],
    },
    7: {
        id: 7, race: "humans", name: "Phoenix", category: "war",
        description: "Heavy plasma warship that can revive fallen allies",
        attack: 19500, life: 58500, weaponType: "plasma", armorType: "heavy",
        hangar: 30, cargo: 2000, speed: 13000, fuelConsumption: 900,
        buildTimeSeconds: 4800,
        cost: { metal: 93900, minerals: 84500, gas: 9400 },
        requirements: [b("shipyard", 9), s("aerodynamics", 6), s("wormholes", 5)],
        skills: ["revival"], ...WAR_TARGETS[7],
    },
    8: {
        id: 8, race: "humans", name: "Ballista", category: "war",
        description: "Siege ship specialized in cracking planetary defenses",
        attack: 13200, life: 39600, weaponType: "laser", armorType: "medium",
        hangar: 22, cargo: 500, speed: 20000, fuelConsumption: 800,
        buildTimeSeconds: 3300,
        cost: { metal: 44000, minerals: 55000, gas: 11000 },
        requirements: [b("shipyard", 8), s("laser", 8), s("plasma", 5)],
        skills: ["artillery"], ...WAR_TARGETS[8],
    },
    9: {
        id: 9, race: "humans", name: "Mammoth", category: "war",
        description: "Moon-sized superweapon capable of destroying planets",
        attack: 700000, life: 2100000, weaponType: "ion", armorType: "heavy",
        hangar: 700, cargo: 1000000, speed: 200, fuelConsumption: 60000,
        buildTimeSeconds: 630000,
        cost: { metal: 2327500, minerals: 1862000, gas: 465500 },
        requirements: [b("shipyard", 14), s("wormholes", 13), s("heavyMetalShields", 10), s("gravitationalManipulators", 1)],
        skills: ["destroyBuildings", "destroyPlanets"], ...WAR_TARGETS[9],
    },
    10: {
        id: 10, race: "humans", name: "Pioneer", category: "civilian",
        description: "Settles new planets, founding a colony on arrival",
        attack: 600, life: 2400, weaponType: "laser", armorType: "heavy",
        hangar: 12, cargo: 7500, speed: 5000, fuelConsumption: 1500,
        buildTimeSeconds: 3480,
        cost: { metal: 12500, minerals: 25000, gas: 10600 },
        requirements: [b("shipyard", 4), s("rockets", 3)],
    },
    11: {
        id: 11, race: "humans", name: "Salvator", category: "civilian",
        description: `Harvests debris and ${RESOURCE_NAMES.scrap} left behind after battles`,
        attack: 50, life: 2200, weaponType: "laser", armorType: "medium",
        hangar: 5, cargo: 40000, speed: 7000, fuelConsumption: 120,
        buildTimeSeconds: 2500,
        cost: { metal: 10500, minerals: 5300, gas: 1800 },
        requirements: [b("shipyard", 4), s("rockets", 6), s("defenceSystems", 2)],
    },
    12: {
        id: 12, race: "humans", name: "Lurker", category: "civilian",
        description: "Expendable reconnaissance probe for scouting enemy planets",
        attack: 1, life: 1, weaponType: "laser", armorType: "light",
        hangar: 1, cargo: 1, speed: 200000000, fuelConsumption: 1,
        buildTimeSeconds: 60,
        cost: { metal: 0, minerals: 1000, gas: 0 },
        requirements: [b("shipyard", 3), s("rockets", 3), s("intelligence", 2)],
    },
    13: {
        id: 13, race: "humans", name: "Orbital Power Plant", category: "service",
        description: "Orbital collector that converts sunlight into planetary energy",
        attack: 1, life: 2200, weaponType: "laser", armorType: "medium",
        hangar: 1, cargo: 0, speed: 0, fuelConsumption: 0,
        buildTimeSeconds: 150,
        cost: { metal: 500, minerals: 2000, gas: 500 },
        requirements: [b("shipyard", 1)],
    },
};
const ROBOT_SHIPS = {
    1: {
        id: 1, race: "robots", name: "Lifter", category: "civilian",
        description: "Light transport robot for moving resources between planets",
        attack: 10, life: 2000, weaponType: "laser", armorType: "medium",
        hangar: 1, cargo: 5000, speed: 22000, fuelConsumption: 12,
        buildTimeSeconds: 600,
        cost: { metal: 2400, minerals: 1400, gas: 0 },
        requirements: [b("shipyard", 2), s("mathematics", 2)],
    },
    2: {
        id: 2, race: "robots", name: "Heavy Lifter", category: "civilian",
        description: "Massive freight platform with the largest hold of its class",
        attack: 20, life: 18200, weaponType: "laser", armorType: "medium",
        hangar: 7, cargo: 50000, speed: 14000, fuelConsumption: 50,
        buildTimeSeconds: 2800,
        cost: { metal: 14900, minerals: 11700, gas: 0 },
        requirements: [b("shipyard", 4), s("astronomy", 6), s("rockets", 6)],
    },
    3: {
        id: 3, race: "robots", name: "Rivet", category: "war",
        description: "Swarm fighter that bolsters the fleet's life",
        attack: 800, life: 2400, weaponType: "laser", armorType: "light",
        hangar: 2, cargo: 160, speed: 24000, fuelConsumption: 20,
        buildTimeSeconds: 800,
        cost: { metal: 2400, minerals: 1600, gas: 0 },
        requirements: [b("shipyard", 1), s("astronomy", 3)],
        skills: ["bonusLife"], ...WAR_TARGETS[3],
    },
    4: {
        id: 4, race: "robots", name: "Piston", category: "war",
        description: "Fast attack platform with devastating critical strikes",
        attack: 2200, life: 6600, weaponType: "ion", armorType: "light",
        hangar: 5, cargo: 850, speed: 30000, fuelConsumption: 300,
        buildTimeSeconds: 1100,
        cost: { metal: 7300, minerals: 6000, gas: 0 },
        requirements: [b("shipyard", 3), s("defenceSystems", 1), s("rockets", 3)],
        skills: ["devastate"], ...WAR_TARGETS[4],
    },
    5: {
        id: 5, race: "robots", name: "Fortress", category: "war",
        description: "Defensive automaton that can slip past enemy armor",
        attack: 3220, life: 18900, weaponType: "laser", armorType: "light",
        hangar: 7, cargo: 1500, speed: 18000, fuelConsumption: 300,
        buildTimeSeconds: 2450,
        cost: { metal: 6200, minerals: 18600, gas: 0 },
        requirements: [b("shipyard", 5), s("rockets", 4), s("ion", 2)],
        skills: ["ignoreArmor"], ...WAR_TARGETS[5],
    },
    6: {
        id: 6, race: "robots", name: "Lattice", category: "war",
        description: "Linked battle group that hardens allied hulls",
        attack: 7800, life: 23400, weaponType: "ion", armorType: "medium",
        hangar: 13, cargo: 1800, speed: 18000, fuelConsumption: 450,
        buildTimeSeconds: 2860,
        cost: { metal: 42800, minerals: 18400, gas: 0 },
        requirements: [b("shipyard", 8), s("aerodynamics", 3)],
        skills: ["armorBoost"], ...WAR_TARGETS[6],
    },
    7: {
        id: 7, race: "robots", name: "Saboteur", category: "war",
        description: "Heavy plasma platform that overcharges allied weapons",
        attack: 18200, life: 54600, weaponType: "plasma", armorType: "heavy",
        hangar: 28, cargo: 2200, speed: 11000, fuelConsumption: 900,
        buildTimeSeconds: 4480,
        cost: { metal: 87600, minerals: 78900, gas: 8800 },
        requirements: [b("shipyard", 9), s("aerodynamics", 6), s("wormholes", 5)],
        skills: ["megaPower"], ...WAR_TARGETS[7],
    },
    8: {
        id: 8, race: "robots", name: "Archer", category: "war",
        description: "Siege automaton specialized in cracking planetary defenses",
        attack: 12000, life: 36000, weaponType: "laser", armorType: "medium",
        hangar: 20, cargo: 400, speed: 18000, fuelConsumption: 750,
        buildTimeSeconds: 3000,
        cost: { metal: 40000, minerals: 50000, gas: 10000 },
        requirements: [b("shipyard", 8), s("plasma", 5)],
        skills: ["artillery"], ...WAR_TARGETS[8],
    },
    9: {
        id: 9, race: "robots", name: "Exterminator", category: "war",
        description: "Colossal superweapon capable of destroying planets",
        attack: 615000, life: 1845000, weaponType: "ion", armorType: "heavy",
        hangar: 615, cargo: 750000, speed: 200, fuelConsumption: 45000,
        buildTimeSeconds: 553500,
        cost: { metal: 2044900, minerals: 1635900, gas: 409000 },
        requirements: [b("shipyard", 14), s("wormholes", 13), s("heavyMetalShields", 10), s("gravitationalManipulators", 1)],
        skills: ["destroyBuildings", "destroyPlanets"], ...WAR_TARGETS[9],
    },
    10: {
        id: 10, race: "robots", name: "Assembler", category: "civilian",
        description: "Settles new planets, founding a colony on arrival",
        attack: 600, life: 2400, weaponType: "laser", armorType: "heavy",
        hangar: 12, cargo: 7500, speed: 5000, fuelConsumption: 1500,
        buildTimeSeconds: 3480,
        cost: { metal: 12500, minerals: 25000, gas: 10600 },
        requirements: [b("shipyard", 4), s("rockets", 3)],
    },
    11: {
        id: 11, race: "robots", name: "Collector", category: "civilian",
        description: `Harvests debris and ${RESOURCE_NAMES.scrap} left behind after battles`,
        attack: 70, life: 3100, weaponType: "laser", armorType: "light",
        hangar: 7, cargo: 60000, speed: 7000, fuelConsumption: 190,
        buildTimeSeconds: 3500,
        cost: { metal: 14700, minerals: 7400, gas: 2500 },
        requirements: [b("shipyard", 4), s("rockets", 6), s("defenceSystems", 2)],
    },
    12: {
        id: 12, race: "robots", name: "Monitor", category: "civilian",
        description: "Expendable reconnaissance drone for scouting enemy planets",
        attack: 1, life: 1, weaponType: "laser", armorType: "medium",
        hangar: 1, cargo: 1, speed: 200000000, fuelConsumption: 1,
        buildTimeSeconds: 60,
        cost: { metal: 0, minerals: 1000, gas: 0 },
        requirements: [b("shipyard", 3), s("rockets", 3), s("intelligence", 2)],
    },
    13: {
        id: 13, race: "robots", name: "Photon Capacitor", category: "service",
        description: "Orbital collector that converts sunlight into planetary energy",
        attack: 1, life: 2200, weaponType: "laser", armorType: "medium",
        hangar: 1, cargo: 0, speed: 0, fuelConsumption: 0,
        buildTimeSeconds: 150,
        cost: { metal: 500, minerals: 2000, gas: 500 },
        requirements: [b("shipyard", 1)],
    },
};
const AXON_SHIPS = {
    1: {
        id: 1, race: "axons", name: "Forager", category: "civilian",
        description: "Organic transport vessel for moving resources between planets",
        attack: 10, life: 4000, weaponType: "laser", armorType: "light",
        hangar: 2, cargo: 15000, speed: 21000, fuelConsumption: 12,
        buildTimeSeconds: 1200,
        cost: { metal: 4800, minerals: 2800, gas: 0 },
        requirements: [b("shipyard", 2), s("mathematics", 2)],
    },
    2: {
        id: 2, race: "axons", name: "Big Forager", category: "civilian",
        description: "Grown freight organism with an expanded cargo sac",
        attack: 10, life: 10400, weaponType: "ion", armorType: "medium",
        hangar: 4, cargo: 30000, speed: 16000, fuelConsumption: 45,
        buildTimeSeconds: 1600,
        cost: { metal: 8500, minerals: 6700, gas: 0 },
        requirements: [b("shipyard", 5), s("mathematics", 4), s("physics", 1)],
        mutatesFrom: { shipId: 1, quantity: 1 }, // 1 Forager per unit
    },
    3: {
        id: 3, race: "axons", name: "Mite", category: "war",
        description: "Nimble bio-fighter that bolsters the fleet's life",
        attack: 400, life: 1200, weaponType: "laser", armorType: "light",
        hangar: 1, cargo: 60, speed: 24000, fuelConsumption: 12,
        buildTimeSeconds: 600,
        cost: { metal: 1200, minerals: 800, gas: 0 },
        requirements: [b("shipyard", 1), s("astronomy", 1)],
        skills: ["bonusLife"], ...WAR_TARGETS[3],
    },
    4: {
        id: 4, race: "axons", name: "Mantix", category: "war",
        description: "Predatory hunter with devastating critical strikes",
        attack: 880, life: 2600, weaponType: "ion", armorType: "light",
        hangar: 2, cargo: 275, speed: 30000, fuelConsumption: 105,
        buildTimeSeconds: 440,
        cost: { metal: 2900, minerals: 2400, gas: 0 },
        requirements: [b("shipyard", 3), s("astronomy", 6)],
        mutatesFrom: { shipId: 3, quantity: 1 }, // 1 Mite per unit
        skills: ["devastate"], ...WAR_TARGETS[4],
    },
    5: {
        id: 5, race: "axons", name: "Chiton", category: "war",
        description: "Defensive organism that can slip past enemy armor",
        attack: 1380, life: 8100, weaponType: "laser", armorType: "light",
        hangar: 3, cargo: 750, speed: 16000, fuelConsumption: 150,
        buildTimeSeconds: 1050,
        cost: { metal: 2700, minerals: 8000, gas: 0 },
        requirements: [b("shipyard", 4), s("defenceSystems", 6), s("rockets", 3)],
        mutatesFrom: { shipId: 3, quantity: 2 }, // 2 Mites per unit
        skills: ["ignoreArmor"], ...WAR_TARGETS[5],
    },
    6: {
        id: 6, race: "axons", name: "Spearhead", category: "war",
        description: "Phasing warform that hardens allied hulls",
        attack: 6000, life: 18000, weaponType: "ion", armorType: "medium",
        hangar: 10, cargo: 750, speed: 16000, fuelConsumption: 500,
        buildTimeSeconds: 2200,
        cost: { metal: 32900, minerals: 14100, gas: 0 },
        requirements: [b("shipyard", 5), s("aerodynamics", 3)],
        mutatesFrom: { shipId: 3, quantity: 3 }, // 3 Mites per unit
        skills: ["armorBoost"], ...WAR_TARGETS[6],
    },
    7: {
        id: 7, race: "axons", name: "Medusa", category: "war",
        description: "Venomous plasma warform that freezes enemy formations",
        attack: 11050, life: 33200, weaponType: "plasma", armorType: "heavy",
        hangar: 17, cargo: 1000, speed: 10000, fuelConsumption: 600,
        buildTimeSeconds: 2720,
        cost: { metal: 53200, minerals: 47900, gas: 5300 },
        requirements: [b("shipyard", 6), s("plasma", 1)],
        mutatesFrom: { shipId: 4, quantity: 1 }, // 1 Mantix per unit
        skills: ["freezing"], ...WAR_TARGETS[7],
    },
    8: {
        id: 8, race: "axons", name: "Bombardier", category: "war",
        description: "Siege organism specialized in cracking planetary defenses",
        attack: 12600, life: 37800, weaponType: "laser", armorType: "medium",
        hangar: 21, cargo: 400, speed: 16000, fuelConsumption: 600,
        buildTimeSeconds: 3150,
        cost: { metal: 42000, minerals: 52500, gas: 10500 },
        requirements: [b("shipyard", 7), s("laser", 10), s("aerodynamics", 3)],
        mutatesFrom: { shipId: 4, quantity: 1 }, // 1 Mantix per unit
        skills: ["artillery"], ...WAR_TARGETS[8],
    },
    9: {
        id: 9, race: "axons", name: "Devoras", category: "war",
        description: "Living superweapon capable of destroying planets",
        attack: 320000, life: 960000, weaponType: "ion", armorType: "heavy",
        hangar: 320, cargo: 300000, speed: 200, fuelConsumption: 30000,
        buildTimeSeconds: 288000,
        cost: { metal: 1064000, minerals: 851200, gas: 212800 },
        requirements: [b("shipyard", 14), s("wormholes", 13), s("heavyMetalShields", 10), s("gravitationalManipulators", 1)],
        mutatesFrom: { shipId: 12, quantity: 20 }, // 20 Synapses per unit
        skills: ["destroyBuildings", "destroyPlanets"], ...WAR_TARGETS[9],
    },
    10: {
        id: 10, race: "axons", name: "Propagator", category: "civilian",
        description: "Settles new planets, founding a colony on arrival",
        attack: 600, life: 2400, weaponType: "laser", armorType: "medium",
        hangar: 12, cargo: 7500, speed: 5000, fuelConsumption: 1500,
        buildTimeSeconds: 3480,
        cost: { metal: 12500, minerals: 25000, gas: 10600 },
        requirements: [b("shipyard", 4), s("rockets", 3)],
    },
    11: {
        id: 11, race: "axons", name: "Miner", category: "civilian",
        description: `Harvests debris and ${RESOURCE_NAMES.scrap} left behind after battles`,
        attack: 50, life: 2200, weaponType: "laser", armorType: "medium",
        hangar: 5, cargo: 40000, speed: 7000, fuelConsumption: 120,
        buildTimeSeconds: 2500,
        // ships.md lists no Gas cost for this variant (unlike the Human/Robot recycling ships).
        cost: { metal: 10500, minerals: 5300, gas: 0 },
        requirements: [b("shipyard", 4), s("rockets", 6), s("defenceSystems", 2)],
    },
    12: {
        id: 12, race: "axons", name: "Synapse", category: "civilian",
        description: "Expendable psychic node for scouting enemy planets",
        attack: 1, life: 1, weaponType: "laser", armorType: "light",
        hangar: 1, cargo: 1, speed: 200000000, fuelConsumption: 1,
        buildTimeSeconds: 60,
        cost: { metal: 0, minerals: 1000, gas: 0 },
        requirements: [b("shipyard", 3), s("rockets", 3), s("intelligence", 2)],
    },
    13: {
        id: 13, race: "axons", name: "Orbital Photosynthesizer", category: "service",
        description: "Photosynthetic organism that converts sunlight into planetary energy",
        attack: 1, life: 2200, weaponType: "laser", armorType: "medium",
        hangar: 1, cargo: 0, speed: 0, fuelConsumption: 0,
        buildTimeSeconds: 150,
        cost: { metal: 500, minerals: 2000, gas: 500 },
        requirements: [b("shipyard", 1)],
    },
};
export const SHIP_DEFINITIONS = {
    humans: HUMAN_SHIPS,
    robots: ROBOT_SHIPS,
    axons: AXON_SHIPS,
};
export function getShipDefinition(race, shipId) {
    const def = SHIP_DEFINITIONS[race]?.[shipId];
    if (!def) {
        throw new Error(`Unknown ship: race=${race} shipId=${shipId}`);
    }
    return def;
}
// ---------------------------------------------------------------------------
// Ship upgrades (Tech Center / Stargate / Space Channel, `techCenter`) — see
// `docs/game-details/ship-upgrades.md`.
// ---------------------------------------------------------------------------
/** Max upgrade level for every upgradable ship. */
export const SHIP_UPGRADE_MAX_LEVEL = 10;
/** Resource cost doubles every level: `cost(N) = cost(1) × 2^(N-1)`. */
export const SHIP_UPGRADE_COST_GROWTH = 2;
/** Upgrade time grows ×1.4 every level: `time(N) = time(1) × 1.4^(N-1)`. */
export const SHIP_UPGRADE_TIME_GROWTH = 1.4;
/** Cancelling a pending upgrade refunds a uniformly-rolled fraction in this range ("Cancel refunds 60–80%"). */
export const SHIP_UPGRADE_REFUND_MIN = 0.6;
export const SHIP_UPGRADE_REFUND_MAX = 0.8;
/** Tech Center time-reduction breakpoints: 5%/level through L5, then 25% + 10%/level above L5, capped at 75% (L10). */
export const TECH_CENTER_TIME_REDUCTION_PER_LEVEL_EARLY = 0.05;
export const TECH_CENTER_TIME_REDUCTION_LATE_BASE = 0.25;
export const TECH_CENTER_TIME_REDUCTION_PER_LEVEL_LATE = 0.10;
export const TECH_CENTER_TIME_REDUCTION_CAP = 0.75;
/** Ships cannot be upgraded at all below this Tech Center level. */
export const TECH_CENTER_MIN_LEVEL_FOR_UPGRADES = 1;
/**
 * Ship IDs that can be upgraded at all: transports (1-2), every war ship
 * (3-9), and the recycling ship (11: Salvator / Collector / Miner) — a Kaeliux-only addition (speed, not
 * part of the original Nemexia design) layered onto the same system. Colony
 * ships (10), spy ships (12), and orbital power plants (13) are never upgradable.
 */
export const UPGRADABLE_SHIP_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11];
export const SHIP_UPGRADE_EFFECT = {
    1: "cargo",
    2: "cargo",
    3: "attackLife",
    4: "attackLife",
    5: "attackLife",
    6: "attackLife",
    7: "attackLife",
    8: "attackLife",
    9: "attackLife",
    11: "speed",
};
/** Percent-of-base gained per upgrade level, by ship ID (race-agnostic — every race's ID-3 fighter upgrades at the same 5%, etc). */
export const SHIP_UPGRADE_PERCENT_PER_LEVEL = {
    1: 0.10, // transports: cargo +10%/level
    2: 0.10,
    3: 0.05, // Vanguard / Rivet / Mite
    4: 0.07, // Saber / Piston / Mantix
    5: 0.08, // Aegis / Fortress / Chiton
    6: 0.08, // Bull / Lattice / Spearhead
    7: 0.11, // Phoenix / Saboteur / Medusa
    8: 0.12, // Ballista / Archer / Bombardier
    9: 0.15, // Mammoth / Exterminator / Devoras
    11: 0.10, // Salvator / Collector / Miner: speed +10%/level (100% at max level 10)
};
function u(metal, minerals, gas, timeSeconds) {
    const cost = {};
    if (metal)
        cost.metal = metal;
    if (minerals)
        cost.minerals = minerals;
    if (gas)
        cost.gas = gas;
    return { cost, timeSeconds };
}
/**
 * Recycling ship (ID 11: Salvator / Collector / Miner) upgrades don't exist in the original Nemexia design, so
 * there is no source-doc time to extract — cost comes from the feature
 * request (10,000 metal / 5,000 minerals / 1,000 gas), and this base time
 * is a hand-picked value sized against the closest comparable-cost war ships
 * (Saber/Aegis, ~16,000 combined level-1 cost, 684-726.75s) rather than
 * a documented figure. Adjust freely if game balance calls for it.
 */
const RECYCLER_UPGRADE_TIME_SECONDS = 720;
/** Level-1 upgrade base cost/time per race/ship — `docs/game-details/ship-upgrades.md` "Unified Base Upgrade Data". */
const HUMAN_SHIP_UPGRADE_BASE = {
    1: u(1000, 0, 0, 641.25),
    2: u(2000, 1000, 0, 769.5),
    3: u(2000, 500, 0, 641.25),
    4: u(10000, 4000, 0, 684),
    5: u(10000, 4000, 0, 726.75),
    6: u(22500, 10000, 2000, 769.5),
    7: u(50000, 25000, 10000, 897.75),
    8: u(25000, 12500, 5000, 834),
    9: u(2000000, 1000000, 500000, 2565),
    11: u(10000, 5000, 1000, RECYCLER_UPGRADE_TIME_SECONDS),
};
const ROBOT_SHIP_UPGRADE_BASE = {
    1: u(1000, 0, 0, 1282.5),
    2: u(4000, 2000, 0, 1539),
    3: u(1800, 400, 0, 1282.5),
    4: u(9000, 4000, 0, 1368),
    5: u(10000, 5000, 0, 1453.5),
    6: u(25000, 12500, 2000, 1539),
    7: u(45000, 22500, 10000, 1795.5),
    8: u(20000, 10000, 4000, 1667.25),
    9: u(2000000, 1000000, 500000, 4617),
    11: u(10000, 5000, 1000, RECYCLER_UPGRADE_TIME_SECONDS),
};
const AXON_SHIP_UPGRADE_BASE = {
    1: u(1600, 800, 0, 1282.5),
    2: u(3000, 1500, 0, 1539),
    3: u(700, 0, 0, 1026.17),
    4: u(3000, 1000, 0, 1282.5),
    5: u(8000, 3300, 0, 1368),
    6: u(12000, 4500, 750, 1453.5),
    7: u(25000, 15000, 1500, 1539),
    8: u(27000, 14000, 1450, 1539),
    9: u(2000000, 1000000, 500000, 4104),
    11: u(10000, 5000, 1000, RECYCLER_UPGRADE_TIME_SECONDS),
};
export const SHIP_UPGRADE_BASE = {
    humans: HUMAN_SHIP_UPGRADE_BASE,
    robots: ROBOT_SHIP_UPGRADE_BASE,
    axons: AXON_SHIP_UPGRADE_BASE,
};
/** Level-1 upgrade cost/time for `race`'s variant of `shipId`, or `undefined` if it isn't upgradable. */
export function getShipUpgradeBase(race, shipId) {
    return SHIP_UPGRADE_BASE[race]?.[shipId];
}
//# sourceMappingURL=ships.js.map